import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import Header from "../../../components/Header";
import { accountsmanagement, caseManagementUrl, clientManagementUrl } from "../../../config";
import {
  allCorporateClients,
  allIndividualClients,
  allCasesView,
  currency,
  // 💡 Assuming this query fetches the necessary document types for the dropdown
  ProformaQuotationDocumentsTypes,
} from "../../../data/Axios/queries";
import { fetchDataEngine, generateAndExecuteMutation, graphqlQuery } from "../../../data/Axios/DynamicService";

const base_url = accountsmanagement.uri;
const client_base_url = clientManagementUrl.uri;
const case_base_url = caseManagementUrl.uri;

const clientTypeOptions = [
  { value: 1, label: "Individual" },
  { value: 2, label: "Corporate" },
];

// Helper function to remove null or empty string keys from an object
const cleanPayload = (data) => {
  return Object.keys(data).reduce((acc, key) => {
    const value = data[key];
    // Keep values that are NOT null AND NOT an empty string
    if (value !== null && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const CreditNoteEntryForm = (props) => {
  const { isEditing = false, data = {}, onClose, refetchData } = props;

  const initialValues = {
    id: data?.id || null,
    clientTypeId: data?.clientTypeId || "",
    // 💡 ADD documentTypeId to initialValues
    documentTypeId: data?.documentTypeId || "",
    clientId: data?.clientId || null,
    caseId: data?.caseId || null,
    dueDate: data?.dueDate
      ? new Date(data.dueDate).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    currencyId: data?.currencyId || null,
    exchangeRate: data?.exchangeRate || 1.0,
    issueDate: data?.issueDate
      ? new Date(data.issueDate).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    amount: data?.amount || 0,
    bankAccountId: data?.bankAccountId || null,
    companyId: data?.companyId || null,
    statusId: data?.statusId || 1,
    createdBy: data?.createdBy || null,
    updatedBy: data?.updatedBy || null,
  };

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  // 💡 ADD documentTypes state
  const [documentTypes, setDocumentTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [allIndividualClientsData, setAllIndividualClientsData] = useState([]);
  const [allCorporateClientsData, setAllCorporateClientsData] = useState([]);
  const [allCasesData, setAllCasesData] = useState([]);

  const filterClients = (clientTypeId, individuals, corporates) => {
    const typeId = parseInt(clientTypeId);
    if (typeId === 1) {
      return individuals.map((c) => ({
        id: c.id,
        name: c.fullName,
        clientTypeId: 1,
      }));
    } else if (typeId === 2) {
      return corporates.map((c) => ({
        id: c.id,
        name: c.companyName,
        clientTypeId: 2,
      }));
    }
    return [];
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 💡 FETCH DOCUMENT TYPES
        const docTypeData = await graphqlQuery(ProformaQuotationDocumentsTypes, base_url);
        console.log("📋 Credit Note - Raw Document Types Response:", docTypeData);

        // Handle both array and object responses
        let documentTypesArray = Array.isArray(docTypeData) ? docTypeData : [];

        console.log("📋 Credit Note - Document Types Array:", documentTypesArray);

        // Filter for Credit Note (ID 7) if it exists
        const creditNoteType = documentTypesArray.find(doc => doc.id === 7);

        if (creditNoteType) {
          console.log("✅ Credit Note - Found Credit Note Type (ID 7):", creditNoteType);
          setDocumentTypes([creditNoteType]);
        } else {
          console.warn("⚠️ Credit Note - ID 7 not found, showing all document types");
          setDocumentTypes(documentTypesArray);
        }

        const returnFields = ["id", "fullName"];
        const inputFields = { isActive: 1 };
        const returnCorporateFields = ["id", "companyName"];
        const individualData = await fetchDataEngine(
          "allIndividualClients",
          "clientModel",
          inputFields,
          returnFields,
          client_base_url
        );
        setAllIndividualClientsData(individualData || []);

        const corporateData = await fetchDataEngine(
          "allCorporateClients",
          "clientModel",
          inputFields,
          returnCorporateFields,
          client_base_url
        );
        setAllCorporateClientsData(corporateData || []);

        const caseData = await graphqlQuery(allCasesView, case_base_url);
        setAllCasesData(caseData || []);

        const currencyData = await graphqlQuery(currency, base_url);
        setCurrencies(currencyData || []);
      } catch (error) {
        console.error("Error fetching form data:", error);
      }
    };
    fetchData();
  }, []);

  // Set clients and cases based on fetched data and props.data
  useEffect(() => {
    if (data?.clientTypeId && allIndividualClientsData.length && allCorporateClientsData.length) {
      const filteredClients = filterClients(data.clientTypeId, allIndividualClientsData, allCorporateClientsData);
      setClients(filteredClients);
    }
    if (data?.clientId && data?.clientTypeId && allCasesData.length) {
      const filteredCases = allCasesData.filter(
        (c) => c.clientId === data.clientId && c.clientTypeId === parseInt(data.clientTypeId)
      );
      setCases(filteredCases);
    }
  }, [data, allIndividualClientsData, allCorporateClientsData, allCasesData]);

  const validationSchema = yup.object().shape({
    clientTypeId: yup.string().required("Client Type is required"),
    // 💡 ADD documentTypeId validation
    documentTypeId: yup.string().required("Document Type is required"),
    clientId: yup.string().required("Client is required"),
    caseId: yup.string().required("Case is required"),
    dueDate: yup.date().required("Due Date is required"),
    currencyId: yup.string().required("Currency is required"),
    // Removed exchangeRate from validation since it's read-only
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const {
        clientTypeId,
        // 💡 DESTRUCTURE documentTypeId
        documentTypeId,
        clientId,
        caseId,
        dueDate,
         currencyId, 
        exchangeRate,
        issueDate,
        amount,
        bankAccountId,
        companyId,
        statusId,
      } = values;

      const baseMutationData = {
        clientTypeId: parseInt(clientTypeId),
        // 💡 INCLUDE documentTypeId in payload and parse it
        documentTypeId: parseInt(documentTypeId),
        clientId: parseInt(clientId),
        caseId: parseInt(caseId),
        // 💡 Format dates to DateTime compatible string

        dueDate: `${dueDate}T12:00:00`,
        issueDate: `${issueDate}T12:00:00`,
        currencyId: parseInt(currencyId),
        exchangeRate: parseFloat(exchangeRate),
        amount: parseFloat(amount),
        // Convert optional IDs to int or keep null
        bankAccountId: bankAccountId ? parseInt(bankAccountId) : null,
        companyId: companyId ? parseInt(companyId) : null,
        isActive: 1,
      };

      // 💡 Use the cleanPayload function to remove null/empty fields
      let finalPayload = cleanPayload(baseMutationData);

      const mutationData = isEditing
        ? { ...finalPayload, id: data?.id, updatedBy: userId }
        : { ...finalPayload, createdBy: userId };

      console.log("🚀 Payload before mutation:", {
        mutationName: isEditing ? "updateCreditNotes" : "createCreditNotes",
        data: mutationData
      });

      const response = await generateAndExecuteMutation(
        isEditing ? "updateCreditNotes" : "createCreditNotes",
        mutationData,
        "creditNotes",
        base_url
      );

      if (response) {
        onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Credit Note ${isEditing ? "updated" : "created"} successfully.`,
        }).then(() => {
          refetchData();
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please ensure all mandatory fields are selected.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Credit Note" : "Create Credit Note"}
        subtitle={isEditing ? "Edit an Existing Credit Note" : "Create a New Credit Note"}
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 4",
                },
              }}
            >
              {/* Client Type Dropdown */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Client Type *</InputLabel>
                <Select
                  name="clientTypeId"
                  value={values.clientTypeId || ""}
                  onChange={(e) => {
                    const newTypeId = e.target.value;
                    setFieldValue("clientTypeId", newTypeId);
                    setFieldValue("clientId", null);
                    setFieldValue("caseId", null);
                    setClients(
                      filterClients(
                        newTypeId,
                        allIndividualClientsData,
                        allCorporateClientsData
                      )
                    );
                    setCases([]);
                  }}
                  onBlur={handleBlur}
                  error={touched.clientTypeId && !!errors.clientTypeId}
                >
                  {clientTypeOptions.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 💡 Document Type Dropdown - Added back based on Proforma form */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Document Type *</InputLabel>
                <Select
                  name="documentTypeId"
                  value={values.documentTypeId || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.documentTypeId && !!errors.documentTypeId}
                >
                  {documentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.documentTypeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Client Dropdown */}
              <Autocomplete
                fullWidth
                options={clients}
                getOptionLabel={(option) => option.name || ""}
                value={clients.find((c) => c.id === values.clientId) || null}
                onChange={(e, newValue) => {
                  setFieldValue("clientId", newValue?.id || null);
                  setFieldValue("caseId", null);
                  if (newValue?.id) {
                    const filteredCases = allCasesData.filter(
                      (c) => c.clientId === newValue.id &&
                        c.clientTypeId === parseInt(values.clientTypeId)
                    );
                    setCases(filteredCases);
                  } else {
                    setCases([]);
                  }
                }}
                disabled={!values.clientTypeId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Client *"
                    error={touched.clientId && !!errors.clientId}
                    helperText={touched.clientId && errors.clientId}
                  />
                )}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Case Dropdown */}
              <Autocomplete
                fullWidth
                options={cases}
                getOptionLabel={(option) => option.title || option.caseNumber || option.id.toString()}
                value={cases.find((c) => c.id === values.caseId) || null}
                onChange={(e, newValue) => setFieldValue("caseId", newValue?.id || null)}
                disabled={!values.clientId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Case *"
                    error={touched.caseId && !!errors.caseId}
                    helperText={touched.caseId && errors.caseId}
                  />
                )}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Due Date */}
              <TextField
                fullWidth
                variant="filled"
                type="date"
                label="Due Date *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.dueDate}
                name="dueDate"
                error={touched.dueDate && !!errors.dueDate}
                helperText={touched.dueDate && errors.dueDate}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Currency Dropdown */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Currency *</InputLabel>
                <Select
                  name="currencyId"
                  value={values.currencyId || ""}
                  onChange={(e) => {
                    const selectedCurrencyId = e.target.value;
                    setFieldValue("currencyId", selectedCurrencyId);
                    const selectedCurrency = currencies.find((curr) => curr.id === parseInt(selectedCurrencyId));
                    setFieldValue("exchangeRate", selectedCurrency?.exchangeRate || 1.0);
                  }}
                  onBlur={handleBlur}
                  error={touched.currencyId && !!errors.currencyId}
                >
                  {currencies.map((curr) => (
                    <MenuItem key={curr.id} value={curr.id}>
                      {curr.currencyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Exchange Rate (Read-only) */}
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Exchange Rate"
                value={values.exchangeRate}
                name="exchangeRate"
                InputProps={{ readOnly: true }}
                disabled
                sx={{ gridColumn: "span 2" }}
              />
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : isEditing ? "Update" : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreditNoteEntryForm;